import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, AlertTriangle, CheckCircle, Clock, Eye, MessageSquare, Gavel, FileText } from 'lucide-react';

const moderationQueue = [
  {
    id: 1,
    type: 'review',
    title: 'Recensione Sospetta - Sakura Sushi',
    author: 'User123',
    reason: 'Punteggi inconsistenti con la descrizione',
    status: 'pending',
    priority: 'high',
    date: '2024-01-15',
    content: 'Ottimo ristorante, servizio perfetto...',
    bushiScore: 95,
    flags: ['Inconsistenza narrativa', 'Primo post utente']
  },
  {
    id: 2,
    type: 'dispute',
    title: 'Contestazione Punteggio - Osteria del Borgo',
    author: 'ChefMarco',
    reason: 'Valutazione ritenuta ingiusta',
    status: 'in_review',
    priority: 'medium',
    date: '2024-01-14',
    content: 'La recensione non riflette la realtà del servizio...',
    originalScore: 45,
    flags: ['Proprietario locale', 'Prima contestazione']
  },
  {
    id: 3,
    type: 'report',
    title: 'Segnalazione Comportamento - Elena Bushido',
    author: 'Sistema',
    reason: 'Multiple recensioni negative concentrate',
    status: 'resolved',
    priority: 'low',
    date: '2024-01-13',
    content: 'Pattern di recensioni negative in breve periodo',
    flags: ['Pattern sospetto', 'Risolto automaticamente']
  }
];

const ritualCodes = {
  rei: {
    name: 'Rei (礼)',
    description: 'Cortesia e rispetto nelle interazioni',
    principles: [
      'Ascoltare tutte le parti coinvolte',
      'Mantenere un tono rispettoso',
      'Riconoscere i meriti di ogni posizione',
      'Evitare giudizi affrettati'
    ]
  },
  makoto: {
    name: 'Makoto (誠)',
    description: 'Sincerità e verità nelle decisioni',
    principles: [
      'Basare le decisioni sui fatti',
      'Essere trasparenti sui criteri',
      'Ammettere quando non si ha certezza',
      'Documentare ogni passaggio'
    ]
  }
};

const disputeFlow = [
  { step: 1, name: 'Triage', description: 'Classificazione automatica e priorità' },
  { step: 2, name: 'Revisione', description: 'Analisi dettagliata da parte dei moderatori' },
  { step: 3, name: 'Decisione', description: 'Applicazione del codice rituale Rei & Makoto' },
  { step: 4, name: 'Appello', description: 'Possibilità di ricorso con log pubblici' }
];

export default function ModerationPage() {
  const [selectedTab, setSelectedTab] = useState('queue');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [moderationDecision, setModerationDecision] = useState('');
  const [moderationNotes, setModerationNotes] = useState('');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-600';
      case 'in_review': return 'bg-blue-600';
      case 'resolved': return 'bg-green-600';
      case 'rejected': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'In Attesa';
      case 'in_review': return 'In Revisione';
      case 'resolved': return 'Risolto';
      case 'rejected': return 'Respinto';
      default: return status;
    }
  };

  const handleModerationAction = (action: string) => {
    console.log('Azione di moderazione:', action, {
      item: selectedItem,
      decision: moderationDecision,
      notes: moderationNotes
    });
    // Qui implementeresti la logica di moderazione
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-bushido text-4xl font-bold mb-4">
            Moderazione & <span className="gold-accent">Dispute</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Sistema di governance basato sui principi di Rei (礼) e Makoto (誠). 
            Trasparenza totale e giustizia procedurale in ogni decisione.
          </p>
        </div>

        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="queue" className="flex items-center space-x-2">
              <Shield className="h-4 w-4" />
              <span>Coda Moderazione</span>
            </TabsTrigger>
            <TabsTrigger value="ritual" className="flex items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>Codice Rituale</span>
            </TabsTrigger>
            <TabsTrigger value="process" className="flex items-center space-x-2">
              <Gavel className="h-4 w-4" />
              <span>Processo Dispute</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center space-x-2">
              <Eye className="h-4 w-4" />
              <span>Log Pubblici</span>
            </TabsTrigger>
          </TabsList>

          {/* Coda Moderazione */}
          <TabsContent value="queue" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Lista Items */}
              <div className="lg:col-span-2 space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Coda di Moderazione</h2>
                  <Badge variant="secondary">
                    {moderationQueue.filter(item => item.status === 'pending').length} in attesa
                  </Badge>
                </div>

                {moderationQueue.map(item => (
                  <Card 
                    key={item.id} 
                    className={`pillar-card cursor-pointer ${selectedItem?.id === item.id ? 'border-secondary' : ''}`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-full bg-muted">
                            {item.type === 'review' && <MessageSquare className="h-4 w-4" />}
                            {item.type === 'dispute' && <AlertTriangle className="h-4 w-4" />}
                            {item.type === 'report' && <Shield className="h-4 w-4" />}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{item.title}</CardTitle>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <span>da {item.author}</span>
                              <span>•</span>
                              <span>{item.date}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Badge className={getPriorityColor(item.priority)} variant="outline">
                            {item.priority}
                          </Badge>
                          <Badge className={getStatusColor(item.status)}>
                            {getStatusLabel(item.status)}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <CardDescription className="mb-3">
                        <strong>Motivo:</strong> {item.reason}
                      </CardDescription>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        {item.flags.map((flag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {flag}
                          </Badge>
                        ))}
                      </div>
                      
                      <p className="text-sm text-muted-foreground truncate">
                        {item.content}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Dettaglio Item */}
              <div className="lg:col-span-1">
                {selectedItem ? (
                  <Card className="sticky top-24">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Dettaglio Moderazione</span>
                        <Badge className={getStatusColor(selectedItem.status)}>
                          {getStatusLabel(selectedItem.status)}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Contenuto</h4>
                        <p className="text-sm bg-muted/50 p-3 rounded">
                          {selectedItem.content}
                        </p>
                      </div>

                      {selectedItem.bushiScore && (
                        <div>
                          <h4 className="font-semibold mb-2">BushiScore</h4>
                          <Badge variant="secondary" className="text-lg">
                            {selectedItem.bushiScore}/100
                          </Badge>
                        </div>
                      )}

                      <div>
                        <h4 className="font-semibold mb-2">Flag Automatici</h4>
                        <div className="space-y-1">
                          {selectedItem.flags.map((flag: string, index: number) => (
                            <Badge key={index} variant="outline" className="text-xs block w-fit">
                              {flag}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {selectedItem.status === 'pending' && (
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium">Decisione</label>
                            <Select value={moderationDecision} onValueChange={setModerationDecision}>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleziona azione" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="approve">Approva</SelectItem>
                                <SelectItem value="reject">Respingi</SelectItem>
                                <SelectItem value="request_changes">Richiedi Modifiche</SelectItem>
                                <SelectItem value="escalate">Escalation</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-sm font-medium">Note Moderazione</label>
                            <Textarea
                              value={moderationNotes}
                              onChange={(e) => setModerationNotes(e.target.value)}
                              placeholder="Aggiungi note sulla decisione..."
                              className="mt-1"
                            />
                          </div>

                          <Button 
                            className="w-full btn-samurai"
                            onClick={() => handleModerationAction(moderationDecision)}
                            disabled={!moderationDecision}
                          >
                            Applica Decisione
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Shield className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        Seleziona un elemento dalla coda per vedere i dettagli
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Codice Rituale */}
          <TabsContent value="ritual" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.entries(ritualCodes).map(([key, ritual]) => (
                <Card key={key} className="pillar-card">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-3">
                      <div className="kanji-display text-4xl">
                        {ritual.name.match(/\((.*?)\)/)?.[1]}
                      </div>
                      <div>
                        <h3 className="font-bushido text-xl">{ritual.name}</h3>
                        <CardDescription>{ritual.description}</CardDescription>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold mb-3">Principi Guida</h4>
                    <div className="space-y-2">
                      {ritual.principles.map((principle, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{principle}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-gradient-to-r from-muted/50 to-muted/30 border-secondary/30">
              <CardHeader>
                <CardTitle>Applicazione del Codice Rituale</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold">Nelle Dispute</h4>
                    <p className="text-sm text-muted-foreground">
                      Ogni disputa viene gestita applicando prima Rei (ascolto rispettoso di tutte le parti) 
                      e poi Makoto (decisione basata sui fatti e trasparenza totale).
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold">Nella Moderazione</h4>
                    <p className="text-sm text-muted-foreground">
                      I moderatori seguono il codice rituale per garantire che ogni decisione sia 
                      giusta, documentata e rispettosa della dignità di tutti gli utenti.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Processo Dispute */}
          <TabsContent value="process" className="space-y-6">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-center">Flusso di Gestione Dispute</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {disputeFlow.map((step, index) => (
                  <Card key={step.step} className="pillar-card text-center">
                    <CardHeader>
                      <div className="mx-auto mb-2">
                        <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {step.step}
                        </div>
                      </div>
                      <CardTitle className="text-lg">{step.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Dettagli del Processo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-semibold">Anti-Gaming</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Rilevamento pattern sospetti</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Verifica cross-reference</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Analisi comportamentale</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="font-semibold">Pesi Credibilità</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>CredScore Alto (90+)</span>
                          <Badge variant="secondary">Peso x1.5</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Badge Verificati</span>
                          <Badge variant="secondary">Peso x1.3</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Storico Positivo</span>
                          <Badge variant="secondary">Peso x1.2</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Nuovo Utente</span>
                          <Badge variant="outline">Peso x0.8</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Log Pubblici */}
          <TabsContent value="logs" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Eye className="mr-2 h-5 w-5" />
                  Log Pubblici delle Decisioni
                </CardTitle>
                <CardDescription>
                  Tutte le decisioni di moderazione sono documentate e pubblicamente accessibili
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    {
                      id: 'LOG-001',
                      date: '2024-01-15 14:30',
                      action: 'Recensione Approvata',
                      moderator: 'Mod_Samurai_01',
                      reason: 'Contenuto conforme ai pilastri, evidenze sufficienti',
                      item: 'Recensione #1247 - Sakura Sushi'
                    },
                    {
                      id: 'LOG-002',
                      date: '2024-01-15 12:15',
                      action: 'Disputa Respinta',
                      moderator: 'Mod_Bushido_02',
                      reason: 'Contestazione non supportata da evidenze valide',
                      item: 'Disputa #89 - Osteria del Borgo'
                    },
                    {
                      id: 'LOG-003',
                      date: '2024-01-14 16:45',
                      action: 'Utente Sospeso',
                      moderator: 'Sistema',
                      reason: 'Pattern di gaming rilevato automaticamente',
                      item: 'Utente #5432'
                    }
                  ].map(log => (
                    <div key={log.id} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-3">
                          <Badge variant="outline">{log.id}</Badge>
                          <span className="font-medium">{log.action}</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {log.date}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Moderatore:</span> {log.moderator}
                        </div>
                        <div>
                          <span className="font-medium">Oggetto:</span> {log.item}
                        </div>
                        <div className="md:col-span-1">
                          <span className="font-medium">Motivo:</span> {log.reason}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-6">
                  <Button variant="outline">
                    Carica Altri Log
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-muted/50 to-muted/30 border-secondary/30">
              <CardHeader>
                <CardTitle>Trasparenza Totale</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Ogni decisione di moderazione è documentata e resa pubblica per garantire 
                  la massima trasparenza del processo. Gli utenti possono sempre verificare 
                  le motivazioni dietro ogni azione intrapresa.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    <span className="font-kanji mr-1">誠</span>
                    Sincerità
                  </Badge>
                  <Badge variant="secondary">
                    <span className="font-kanji mr-1">義</span>
                    Giustizia
                  </Badge>
                  <Badge variant="secondary">
                    <span className="font-kanji mr-1">礼</span>
                    Rispetto
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}